import { Select, MenuItem, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';

type GroupByListItem = {
  value: string;
  label: string;
};

type Props = {
  value: string;
  onChange: (next: string) => void;
  groupByList: GroupByListItem[];
  includeGroupByMenuColumns?: string[];
};

export const PlatformHealthGroupByDropdown: React.FC<Props> = ({
  value,
  onChange,
  groupByList,
  includeGroupByMenuColumns = []
}) => {
  const theme = useTheme();
  const colors = theme.palette;

  const filteredGroupByList = includeGroupByMenuColumns.length > 0
    ? groupByList.filter((opt) => includeGroupByMenuColumns.includes(opt.value))
    : groupByList;

  const menuItemSx = {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: "14px",
    justifyContent: "space-between",
    backgroundColor: "transparent !important",
    color: `${colors.DropdownV2.menuItemText} !important`,
    "&&.Mui-selected": {
      backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
      color: `${colors.DropdownV2.menuItemSelectedText} !important`,
    },
    "&&.Mui-selected:hover": {
      backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
      color: `${colors.DropdownV2.menuItemSelectedText} !important`,
    },
    "&:hover": {
      backgroundColor: `${colors.DropdownV2.menuItemHover} !important`,
      color: `${colors.DropdownV2.menuItemHoverText} !important`,
    },
  };

  if (filteredGroupByList.length === 0 && includeGroupByMenuColumns.length > 0) {
    return null;
  }

  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      IconComponent={ExpandMoreIcon}
      displayEmpty
       renderValue={(selected) => {
        const selectedOption = filteredGroupByList.find(opt => opt.value === selected);
        const selectedLabel = selected === 'none'
          ? 'None'
          : selectedOption?.label || 'Group By';

        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              component="span"
              sx={{
                color: colors.DropdownV2.menuItemText,
                fontWeight: 300,
              }}
            >
              Group By:&nbsp;
            </Box>
            <Box
              component="span"
              sx={{
                color: colors.DropdownV2.menuItemText,
                fontWeight: 500,
              }}
            >
              {selectedLabel}
            </Box>
          </Box>
        );
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
            color: `${colors.DropdownV2.menuItemText} !important`,
            mt: 1,
            borderRadius: "4px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.12) !important",
          },
        },
        MenuListProps: {
          sx: {
            paddingTop: "0 !important",
            paddingBottom: "0 !important",
            backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
          },
        },
      }}
      sx={{
        fontFamily: 'Montserrat, sans-serif',
        fontSize: "14px",
        width: 242,
        height: 50,
        borderRadius: "4px",
        background: "transparent !important",
        color: colors.DropdownV2.menuItemText,
        "& .MuiSelect-select": {
          padding: "0 14px !important",
          color: `${colors.DropdownV2.menuItemText} !important`,
          height: "50px",
          display: "flex",
          alignItems: "center",
          boxSizing: "border-box",
        },
        "& .MuiSelect-icon": {
          color: colors.DropdownV2.dropdownIconDefault,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: colors.DropdownV2.borderColor,
          borderWidth: "0.8px",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: colors.DropdownV2.borderColorHover,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: colors.DropdownV2.borderColorActive,
          borderWidth: "1px",
        },
        "&.Mui-focused .MuiSelect-icon": {
          color: colors.DropdownV2.dropdownIconActive,
        },
      }}
    >
      <MenuItem value="none" sx={menuItemSx}>
        <span>None</span>
        {value === 'none' && <CheckIcon sx={{ color: colors.DropdownV2.tickColor, fontSize: "16px", ml: 2 }} />}
      </MenuItem>

      {filteredGroupByList.map((opt) => {
        const isSelected = value === opt.value;
        return (
          <MenuItem
            key={opt.value}
            value={opt.value}
            sx={menuItemSx}
          >
            <span>{opt.label}</span>
            {isSelected && <CheckIcon sx={{ color: colors.DropdownV2.tickColor, fontSize: "16px", ml: 2 }} />}
          </MenuItem>
        );
      })}
    </Select>
  );
};